import { io, Socket } from 'socket.io-client';
import SimplePeer from 'simple-peer';
import EventEmitter from './EventEmitter';

const DEFAULT_ICE_SERVERS = [
  {
    urls: 'turn:58.123.12.2',
    username: 'coturn',
    credential: 'coturn',
  },
  { urls: 'stun:stun.l.google.com:19302' }
];

class Peer extends EventEmitter {
  public id?: string;
  public code?: string;
  private socket?: Socket;
  public peers: Record<string, SimplePeer.Instance> = {};
  public isHost: boolean = false;
  public isConnecting: boolean = false;

  public async connectSignalSocket(url: string, channel: string, code?: string): Promise<string | undefined> {
    if (this.isConnecting || this.socket?.connected) return;

    this.isConnecting = true;
    this.close();

    const timeout = new Promise((resolve) => {
      setTimeout(() => {
        resolve(false);
      }, 5000);
    });

    const connect = new Promise((resolve) => {
      const onError = (): void => {
        resolve(false);
      };

      this.socket = io(url, {
        auth: {
          code,
          channel,
        },
      });

      this.socket.once('error', onError);
      this.socket.once('connect', () => {
        if (this.socket) {
          this.socket.once('code', (code: string): void => {
            if (this.socket) {
              this.id = this.socket.id;
              this.code = code;
              this.socket.off('error', onError);
              this.socket.on('message', (event: string, ...args) => this.onSocketMessage(event, ...args));
              resolve(true);
            } else {
              resolve(false);
            }
          });
        } else {
          resolve(false);
        }
      });
    });

    const connected = await Promise.race([timeout, connect]);

    this.isConnecting = false;

    if (connected && this.code) {
      return this.code;
    } else {
      this.destroySignalSocket();
      throw new Error('connection fail');
    }
  }

  public async connectPeer(code: string): Promise<void> {
    if (this.socket) {
      this.socket.send('join', code);
      await this.createPeer(code);
    } else {
      throw new Error('socket is null');
    }
  }

  public send<T>(code: string, data: T): void {
    if (this.peers[code]) {
      this.peers[code].send(JSON.stringify(data));
    }
  }

  public broadcast<T>(data: T): void {
    const message = JSON.stringify(data);

    for (const code in this.peers) {
      this.peers[code].send(message);
    }
  }

  public close(): void {
    this.destroySignalSocket();

    for (const code in this.peers) {
      this.destroyPeer(code);
    }
  }

  private signal(id: string, data: SimplePeer.SignalData): void {
    this.peers[id].signal(data);
  }

  private onPeerMessage<T>(code: string, message: Buffer): void {
    const data: T = JSON.parse(message.toString('utf-8'));
    this.emit('message', code, data);
  }

  private onSocketMessage(event: string, ...args: Array<unknown>): void {
    switch (event) {
      case 'signal':
        this.signal(args[0] as string, args[1] as SimplePeer.SignalData);
        break;
      case 'join':
        this.createPeer(args[0] as string, true);
        break;
      default:
        throw new Error('unknown event');
    }
  }

  private createPeer(code: string, isHost: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      const MAX_USER_COUNT = 1;
      if (this.peers[code] || Object.keys(this.peers).length >= MAX_USER_COUNT) resolve();

      let conencted: boolean = false;
      this.peers[code] = new SimplePeer({
        initiator: isHost,
        config: {
          iceServers: DEFAULT_ICE_SERVERS,
        },
      });

      const destroy = (e: Error) => {
        if (!isHost) {
          this.destroySignalSocket();
        }

        this.destroyPeer(code);

        this.emit('disconnected', code);
        reject(e);
      };

      const onSignal = (data: SimplePeer.SignalData) => {
        if (this.socket) {
          this.socket.send('signal', code, data);
        } else {
          throw new Error('socket is null');
        }
      };

      const onConnect = () => {
        conencted = true;
        this.isHost = isHost;
        this.emit('connected', code);
        if (!isHost) {
          this.destroySignalSocket();
        }
        this.peers[code].off('signal', onSignal);

        resolve();
      };

      this.peers[code].on('data', (message: Buffer) => this.onPeerMessage(code, message));
      this.peers[code].on('signal', onSignal);
      this.peers[code].once('error', destroy);
      this.peers[code].once('close', destroy);
      this.peers[code].once('end', destroy);
      this.peers[code].once('connect', onConnect);

      setTimeout(() => {
        if (!conencted && this.peers[code]) {
          destroy(new Error('connection timeout'));
        }
      }, 5000);
    });
  }

  public destroyPeer(code: string): void {
    if (this.peers[code]) {
      this.peers[code].removeAllListeners();
      this.peers[code].destroy();
      delete this.peers[code];
    }
  }

  public destroySignalSocket(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.close();
      this.isConnecting = false;
    }
  }
}

export default Peer;
